/**
 * risk.mypcb.com page list for the shared panel shell (redesign 0.24.0, Bill 2026-11-03). One
 * entry per routed page in App.tsx; the role lists repeat the route gates there (a hidden page is a
 * convenience only, the API checks every call). There are no platforms here, so no switcher.
 */

import type { ShellNav, ShellPage, ShellTask } from '@retail-ops/panel-shell';

// Same lists as App.tsx's nested RoleGates.
export const DISCLOSURE_ROLES = ['DisclosureManager', 'RiskManager', 'Admin', 'SuperAdmin'] as const;
export const PACKAGE_PREVIEW_ROLES = ['RiskManager', 'DisclosureManager', 'Risk', 'Admin', 'SuperAdmin'] as const;
export const RISK_APPROVALS_ROLES = ['RiskApprover', 'RiskManager', 'Admin', 'SuperAdmin'] as const;

/** Every change to settings, disclosures and configuration is logged; the history lives in the Admin portal. */
export const CHANGE_HISTORY_URL = 'https://admin.mypcb.com/history';

interface RiskPage extends ShellPage {
  roles?: ReadonlyArray<string>;
  /** Only while SHOW_UNPROVEN is on (App.tsx). */
  unproven?: boolean;
}

const PAGES: ReadonlyArray<RiskPage> = [
  // Reviews
  {
    path: '/verifications',
    label: 'Verifications',
    section: 'reviews',
    help: 'Identity verification (LexisNexis) results holding an application: review the report and the applicant, then clear the result (the application is released) or decline it (do not open).',
    synonyms: ['identity', 'lexisnexis hold', 'clear', 'decline', 'review'],
  },
  {
    path: '/sanctions',
    label: 'Sanctions screening',
    section: 'reviews',
    help: 'Clear or confirm held screenings (Reviews), and the government sanctions lists account opening screens against: what is loaded and current, reload, test a name, calibrate the score lines, and what Core Director’s nightly OFAC report will show.',
    synonyms: ['ofac', 'sdn', 'watch list', 'screening', 'test a name'],
  },
  // Settings
  {
    path: '/lexisnexis',
    label: 'LexisNexis FraudPoint',
    section: 'settings',
    help: 'The FraudPoint cut-offs that decide proceed, follow-up or decline, what bankers see in the identity report, and Verification API testing: run the LexisNexis and OFAC checks yourself and see what drives each result.',
    synonyms: ['fraudpoint', 'cut-off', 'score', 'identity report', 'test'],
  },
  {
    path: '/risk-settings',
    label: 'Risk settings',
    section: 'settings',
    help: 'Every setting that changes a risk or compliance result: FraudPoint cut-offs and gate enforcement, identity-check inputs, OFAC re-screening, the PEP control, signing and disclosure rules, and remote signing limits. Each change needs a reason and is logged.',
    synonyms: ['pep', 'gate', 'remote signing', 'rescreen', 'options'],
  },
  // Disclosures
  {
    path: '/disclosures',
    label: 'Disclosure library',
    section: 'disclosures',
    roles: DISCLOSURE_ROLES,
    help: 'The disclosure library: templates and their versions (submit, approve, activate, retire), variants, sections, merge fields, and which disclosures go with which products and steps.',
    synonyms: ['disclosures'],
  },
  {
    path: '/disclosures/templates',
    label: 'Templates',
    section: 'disclosures',
    roles: DISCLOSURE_ROLES,
    help: 'Browse, filter and edit disclosure templates. Open a template to manage its versions.',
    synonyms: ['template', 'version', 'activate', 'retire'],
  },
  { path: '/disclosures/variants', label: 'Variants', section: 'disclosures', roles: DISCLOSURE_ROLES, help: 'The version of a disclosure each customer type gets.', synonyms: ['customer type'] },
  { path: '/disclosures/sections', label: 'Sections', section: 'disclosures', roles: DISCLOSURE_ROLES, help: 'Reusable pieces of disclosure text, written once and included in several disclosures.', synonyms: ['fragment', 'reusable text'] },
  { path: '/disclosures/merge-fields', label: 'Merge fields', section: 'disclosures', roles: DISCLOSURE_ROLES, help: 'The fill-in fields a disclosure can use, such as the customer’s name, and how each is formatted.', synonyms: ['tokens', 'placeholders', 'fields'] },
  { path: '/disclosures/workflow-links', label: 'Workflow links', section: 'disclosures', roles: DISCLOSURE_ROLES, help: 'Which disclosures attach at each step of account opening.', synonyms: ['which disclosures', 'step'] },
  { path: '/disclosures/approval-queue', label: 'Approval queue', section: 'disclosures', roles: DISCLOSURE_ROLES, help: 'Draft disclosure versions waiting for review: approve, or reject with a reason.', synonyms: ['approve', 'review', 'draft'] },
  {
    path: '/package-generator',
    label: 'Package generator',
    section: 'disclosures',
    roles: PACKAGE_PREVIEW_ROLES,
    help: 'Describe a test applicant and see the exact package signing would build: each document, why it is in or out, who signs, and the PDFs. No applications, customers or accounts are created.',
    synonyms: ['preview', 'test package', 'what will print'],
  },
  // Records
  {
    path: CHANGE_HISTORY_URL,
    label: 'Change history',
    section: 'records',
    external: true,
    help: 'Every change to settings, disclosures and configuration: who, when, before and after, and why. Opens in the Admin portal.',
    synonyms: ['audit', 'restore', 'undo', 'who changed'],
  },
  // Hidden until runtime-proven (App.tsx SHOW_UNPROVEN)
  { path: '/queue', label: 'Approvals queue', section: 'approvals', roles: RISK_APPROVALS_ROLES, unproven: true },
  { path: '/history', label: 'Approvals history', section: 'approvals', roles: RISK_APPROVALS_ROLES, unproven: true },
  { path: '/approvers', label: 'Approvers', section: 'approvals', roles: RISK_APPROVALS_ROLES, unproven: true },
  { path: '/adverse-action-queue', label: 'Adverse-action queue', section: 'approvals', unproven: true },
  { path: '/audit-trail', label: 'Application audit trail', section: 'approvals', unproven: true },
];

const SECTIONS: ReadonlyArray<{ id: string; label: string; holds: string }> = [
  { id: 'reviews', label: 'Reviews', holds: 'Results holding an application, waiting for Risk Management to decide.' },
  { id: 'settings', label: 'Settings', holds: 'Settings that change a risk or compliance result. Each change needs a reason and is logged.' },
  { id: 'disclosures', label: 'Disclosures', holds: 'The disclosure library and the package generator.' },
  { id: 'approvals', label: 'Approvals', holds: 'Approval requests and approvers.' },
  { id: 'records', label: 'Records', holds: 'The record of every change.' },
];

export const RISK_HOME_HELP =
  'Risk Management owns these settings and content for account opening. Changes apply to the next application; nothing already decided is re-graded. Use the menu or the search to open a page.';

export function riskNav(roles: ReadonlyArray<string>, showUnproven: boolean): ShellNav {
  const can = (pg: RiskPage): boolean => (!pg.unproven || showUnproven) && (!pg.roles || pg.roles.some((r) => roles.includes(r)));
  return {
    root: { label: 'Risk Management', path: '/' },
    homeHelp: RISK_HOME_HELP,
    sections: SECTIONS.map((s) => ({
      id: s.id,
      label: s.label,
      holds: s.holds,
      pages: PAGES.filter((pg) => pg.section === s.id && can(pg)).map(({ roles: _r, unproven: _u, ...pg }) => pg),
    })).filter((s) => s.pages.length > 0),
  };
}

const TASKS: ReadonlyArray<ShellTask> = [
  {
    id: 'verify',
    title: 'Clear or decline an identity hold',
    blurb: 'LexisNexis results holding an application: review the report, then clear or decline.',
    path: '/verifications',
    icon: 'shield',
    crumb: 'Reviews › Verifications',
  },
  {
    id: 'sanctions',
    title: 'Clear or confirm a sanctions hit',
    blurb: 'Held screenings, the lists loaded, and testing a name.',
    path: '/sanctions',
    icon: 'alert',
    crumb: 'Reviews › Sanctions screening',
    synonyms: ['ofac'],
  },
  {
    id: 'cutoffs',
    title: 'Change a FraudPoint cut-off',
    blurb: 'The scores that decide proceed, follow-up or decline.',
    path: '/lexisnexis',
    icon: 'sliders',
    crumb: 'Settings › LexisNexis FraudPoint',
  },
  {
    id: 'settings',
    title: 'Change a risk setting',
    blurb: 'Gate enforcement, OFAC re-screening, the PEP control, signing and disclosure rules.',
    path: '/risk-settings',
    icon: 'settings',
    crumb: 'Settings › Risk settings',
  },
  {
    id: 'approve-disclosure',
    title: 'Approve a disclosure version',
    blurb: 'Drafts waiting for review: approve, or reject with a reason.',
    path: '/disclosures/approval-queue',
    icon: 'file',
    crumb: 'Disclosures › Approval queue',
  },
  {
    id: 'package',
    title: 'Preview a disclosure package',
    blurb: 'Describe a test applicant and see exactly what signing would build. Nothing is created.',
    path: '/package-generator',
    icon: 'eye',
    crumb: 'Disclosures › Package generator',
  },
  {
    id: 'history',
    title: 'See what changed, and undo a change',
    blurb: 'Every change, yours and everyone else’s: who, when, before and after, and why. Opens in the Admin portal.',
    path: CHANGE_HISTORY_URL,
    icon: 'history',
    crumb: 'Records › Change history',
    external: true,
    tour: 'tile-change-history',
  },
];

/** Tasks whose page the person can open. */
export function riskTasksFor(nav: ShellNav): ShellTask[] {
  const paths = new Set(nav.sections.flatMap((s) => s.pages.map((p) => p.path)));
  return TASKS.filter((t) => paths.has(t.path));
}
